import { useCallback, useEffect, useState } from 'react';
import { api, restoreSession, setTokens, type Me } from './api.ts';
import { AppProvider, useApp } from './store.tsx';
import { AuthScreen } from './screens/Auth.tsx';
import { OnboardingScreen } from './screens/Onboarding.tsx';
import { HomeScreen } from './screens/Home.tsx';
import { TransactionsScreen, TransactionDetail, AddTransactionSheet } from './screens/Transactions.tsx';
import { InsightsScreen } from './screens/Insights.tsx';
import { PlanScreen } from './screens/Plan.tsx';
import { AssistantScreen } from './screens/Assistant.tsx';
import { GoalsScreen } from './screens/Goals.tsx';
import { SettingsScreen } from './screens/Settings.tsx';
import { ImportScreen } from './screens/Import.tsx';
import { Splash } from './components.tsx';
import { AppLock } from './AppLock.tsx';
import * as native from './native.ts';
import { EmailLanding, detectLanding } from './screens/EmailLanding.tsx';
import { PhoneNotConnectedBanner, UpdateReadyBanner } from './screens/ConnectPhone.tsx';

type Tab = 'home' | 'transactions' | 'plan' | 'insights' | 'assistant' | 'goals' | 'settings';

export default function App(): JSX.Element {
  return (
    <AppProvider>
      <Root />
    </AppProvider>
  );
}

function Root(): JSX.Element {
  const { user, setUser, t } = useApp();
  const [booting, setBooting] = useState(true);
  // Read once, before anything can rewrite the address bar.
  const [landing, setLanding] = useState(detectLanding);

  // Restore a session from the stored refresh token before rendering anything,
  // so a reload does not flash the sign-in screen at an authenticated user.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (await restoreSession()) {
          const me = await api.get<Me>('/me');
          if (!cancelled) setUser(me);
        }
      } catch {
        setTokens(null, null);
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => { cancelled = true; };
  }, [setUser]);

  if (booting) return <Splash label={t('tagline')} />;

  // Both emailed links must work with no session: someone who has forgotten
  // their password cannot be asked to sign in first.
  if (landing) {
    return (
      <EmailLanding
        kind={landing.kind}
        token={landing.token}
        onDone={() => setLanding(null)}
      />
    );
  }

  if (!user) return <AuthScreen onAuthenticated={setUser} />;

  if (!user.onboardingComplete) {
    return <OnboardingScreen onComplete={async () => setUser(await api.get<Me>('/me'))} />;
  }

  return (
    <AppLock>
      <Shell />
    </AppLock>
  );
}

function Shell(): JSX.Element {
  const { t, setUser, setActiveAccountId } = useApp();
  const [tab, setTab] = useState<Tab>('home');
  const [openTransaction, setOpenTransaction] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>();
  const [refreshKey, setRefreshKey] = useState(0);
  // Whether Activity is narrowed to the transactions that need attention.
  const [reviewOnly, setReviewOnly] = useState(false);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Tapping a native notification should land on what it was about.
  //
  // The payload carries the same `url` the Web Push notification does — a
  // path with a `tab` query — but nothing in the native shell was reading it,
  // so an alert opened the app on Home and left the user to find the thing
  // themselves. Web Push handles this in the service worker; APNs has no
  // service worker, so it has to be handled here.
  useEffect(() => {
    void native.onNotificationTap((url) => {
      const target = new URLSearchParams(url.split('?')[1] ?? '').get('tab');
      const tabs: Tab[] = ['home', 'transactions', 'plan', 'insights', 'assistant', 'goals', 'settings'];
      if (target && (tabs as string[]).includes(target)) setTab(target as Tab);
    });
  }, []);

  const goToCategory = (category: string) => {
    setCategoryFilter(category);
    setTab('transactions');
  };

  const signedOut = () => {
    setTokens(null, null);
    setUser(null);
    // Belt and braces alongside the check on Home: the account filter belongs
    // to whoever was signed in, and must not follow the next person.
    setActiveAccountId(null);
  };

  const TABS: { key: Tab; icon: string; label: string }[] = [
    { key: 'home', icon: '◎', label: t('navHome') },
    { key: 'transactions', icon: '≡', label: t('navTransactions') },
    { key: 'plan', icon: '◳', label: t('navPlan') },
    { key: 'insights', icon: '◫', label: t('navInsights') },
    { key: 'assistant', icon: '✦', label: t('navAssistant') },
    { key: 'goals', icon: '◈', label: t('navGoals') },
    { key: 'settings', icon: '⚙', label: t('navSettings') },
  ];

  return (
    <div className="app">
      {/* Above the tab content and outside its remount key, so switching tabs
          does not make it flash. */}
      <UpdateReadyBanner />
      <PhoneNotConnectedBanner />

      <main key={`${tab}-${refreshKey}`}>
        {tab === 'home' && (
          <HomeScreen
            onOpenTransaction={setOpenTransaction}
            onSeeAll={() => { setCategoryFilter(undefined); setTab('transactions'); }}
            onOpenCategory={goToCategory}
          />
        )}
        {tab === 'transactions' && (
          <TransactionsScreen
            initialCategory={categoryFilter}
            /* Held here rather than in the screen because fixing a flagged
               transaction bumps refreshKey, which remounts <main> — so screen
               state would be thrown away exactly when someone is working
               through the queue one row at a time. */
            reviewOnly={reviewOnly}
            onReviewOnlyChange={setReviewOnly}
            onOpen={setOpenTransaction}
            onAdd={() => setShowAdd(true)}
          />
        )}
        {tab === 'plan' && <PlanScreen onOpenTransaction={setOpenTransaction} />}
        {tab === 'insights' && <InsightsScreen />}
        {tab === 'assistant' && <AssistantScreen />}
        {tab === 'goals' && <GoalsScreen />}
        {tab === 'settings' && (
          <SettingsScreen onSignedOut={signedOut} onOpenImport={() => setShowImport(true)} />
        )}
      </main>

      <nav className="nav" aria-label="Main">
        {TABS.map((item) => (
          <button
            key={item.key}
            className={`nav__item${tab === item.key ? ' nav__item--active' : ''}`}
            onClick={() => {
              setTab(item.key);
              if (item.key !== 'transactions') { setCategoryFilter(undefined); setReviewOnly(false); }
            }}
            aria-current={tab === item.key ? 'page' : undefined}
          >
            <span className="nav__icon" aria-hidden>{item.icon}</span>
            {item.label}
          </button>
        ))}
      </nav>

      {openTransaction && (
        <TransactionDetail
          id={openTransaction}
          onClose={() => setOpenTransaction(null)}
          onChanged={refresh}
        />
      )}
      {showAdd && <AddTransactionSheet onClose={() => setShowAdd(false)} onSaved={refresh} />}
      {showImport && <ImportScreen onClose={() => setShowImport(false)} onImported={refresh} />}
    </div>
  );
}
